import { useEffect, useRef } from "react";

import { useRouter } from "next/router";

import type { ParsedUrlQuery } from "querystring";

import {
  PRO_BUYER_URL_PARAM,
  PRO_BUYER_URL_VALUE,
} from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import { SEARCH_ROUTE_PARAM } from "@/features/search/api/route";

/**
 * WHO-5953 F1. Main-menu category links and the search box build their own URLs, so
 * `proBuyer=1` fell off on every category or search switch. The choice is remembered
 * per browser session and put back client-side, for signed-in Pro Buyers only: the
 * server render and the cache of public pages stay the same for everyone.
 */
export const PRO_BUYER_FILTER_STORAGE_KEY = "whoppah:pro-buyer-filter";

export const readRememberedProBuyerFilter = (): boolean => {
  try {
    return (
      window.sessionStorage.getItem(PRO_BUYER_FILTER_STORAGE_KEY) ===
      PRO_BUYER_URL_VALUE
    );
  } catch {
    return false;
  }
};

export const rememberProBuyerFilter = (on: boolean) => {
  try {
    if (on) {
      window.sessionStorage.setItem(
        PRO_BUYER_FILTER_STORAGE_KEY,
        PRO_BUYER_URL_VALUE
      );
    } else {
      window.sessionStorage.removeItem(PRO_BUYER_FILTER_STORAGE_KEY);
    }
  } catch {
    /* Storage blocked (private mode, site data off): the URL alone still works. */
  }
};

/** Which page this is: route, landing segment and search term. Filters are not identity. */
export const searchPageIdentity = (pathname: string, query: ParsedUrlQuery) =>
  JSON.stringify([
    pathname,
    query[SEARCH_ROUTE_PARAM] ?? null,
    query.query ?? null,
  ]);

export type ProBuyerFilterAction = "remember" | "forget" | "restore" | "none";

/**
 * - filter in the URL: remember it.
 * - filter gone on the same page (Toon alles, clear all): the buyer turned it off.
 * - filter gone on a new page while remembered: put it back.
 */
export const decideProBuyerFilterAction = ({
  inUrl,
  remembered,
  samePage,
}: {
  inUrl: boolean;
  remembered: boolean;
  samePage: boolean;
}): ProBuyerFilterAction => {
  if (inUrl) {
    return "remember";
  }

  if (!remembered) {
    return "none";
  }

  return samePage ? "forget" : "restore";
};

export const useRememberProBuyerFilter = (isProBuyer: boolean) => {
  const router = useRouter();
  const { asPath, isReady, pathname, query } = router;
  const previousIdentity = useRef<string | null>(null);
  /* Decide once per URL: re-renders on the same URL must not read as "turned off". */
  const handledPath = useRef<string | null>(null);

  useEffect(() => {
    if (!isProBuyer || !isReady || handledPath.current === asPath) {
      return;
    }

    handledPath.current = asPath;

    const identity = searchPageIdentity(pathname, query);
    const action = decideProBuyerFilterAction({
      inUrl: query[PRO_BUYER_URL_PARAM] === PRO_BUYER_URL_VALUE,
      remembered: readRememberedProBuyerFilter(),
      samePage: previousIdentity.current === identity,
    });

    previousIdentity.current = identity;

    if (action === "remember") {
      rememberProBuyerFilter(true);
    } else if (action === "forget") {
      rememberProBuyerFilter(false);
    } else if (action === "restore") {
      void router.replace(
        {
          pathname,
          query: { ...query, [PRO_BUYER_URL_PARAM]: PRO_BUYER_URL_VALUE },
        },
        undefined,
        { shallow: true, scroll: false }
      );
    }
  }, [asPath, isProBuyer, isReady, pathname, query, router]);
};
